import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, Prompt } from 'react-router-dom';
import { useSystemPopupQueue, POPUP_KEYS } from '../hooks/useSystemPopupQueue';
import PasswordChangeFlow from './PasswordChangeFlow';
import CompliancePopup from './CompliancePopup';
import PasskeyOnboardingModal from './PasskeyOnboardingModal';
import { clearPasskeyPromptFromStoredUser } from '../_helpers/passkeyOnboarding';
import { userService } from '../_services';
import { SESSION_ENDED_EVENT } from '../sessionEndedEvents';

const AUTH_PATH_PREFIXES = ['/login', '/resetPassword', '/passwordChanged'];

// Must be >= dialog animation duration (duration-75 = 75ms). Extra buffer for safety.
const CLOSE_MS = 120;

function isAuthRoute(pathname) {
    return AUTH_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/**
 * Single post-login popup orchestrator.
 * Priority: P1 Password change (mandatory) → P2 Passkey → P3 Compliance.
 *
 * P1 uses PasswordChangeFlow — a single Dialog that cross-fades between the nudge
 * screen and the password form, so the overlay never blinks during the transition.
 *
 * P2/P3 use graceful close: dialogOpen=false lets the Radix exit animation play
 * before the component tree changes.
 */
export default function SystemPopupOrchestrator() {
    const location = useLocation();
    const [user, setUser] = useState(null);

    // Controls the `open` prop of the P2/P3 dialogs
    const [dialogOpen, setDialogOpen] = useState(false);

    // Track pending timers so we can cancel on unmount / early teardown
    const timerRef = useRef([]);
    const addTimer = useCallback((fn, ms) => {
        const id = setTimeout(fn, ms);
        timerRef.current.push(id);
        return id;
    }, []);
    useEffect(() => () => { timerRef.current.forEach(clearTimeout); }, []);

    // Guard against multiple gracefulClose calls for the same queue step
    // (e.g. if both onRegistered and onOpenChange fire a dismiss concurrently).
    const dismissingRef = useRef(false);

    // When the session ends (timeout, 401, or account termination by wholesaler),
    // immediately tear down all orchestrator state so no post-login popup
    // (Passkey, Compliance, Password) can appear on top of the session-ended modal.
    // The route hasn't changed yet at this point, so the location-based useEffect
    // below would not fire — this listener is the only reliable teardown path.
    useEffect(() => {
        const shutdown = () => {
            // Cancel any pending open-dialog timers (e.g. the 30 ms Passkey open delay)
            timerRef.current.forEach(clearTimeout);
            timerRef.current = [];
            setDialogOpen(false);
            setUser(null);
        };
        window.addEventListener(SESSION_ENDED_EVENT, shutdown);
        return () => window.removeEventListener(SESSION_ENDED_EVENT, shutdown);
    }, []);

    // Read user from localStorage; reset on auth routes
    useEffect(() => {
        if (isAuthRoute(location.pathname)) {
            setUser(null);
            setDialogOpen(false);
            return;
        }
        try {
            const raw = localStorage.getItem('user');
            if (!raw || raw === '{}' || raw === 'null') { setUser(null); setDialogOpen(false); return; }
            const u = JSON.parse(raw);
            if (u?.id) setUser(u);
            else { setUser(null); setDialogOpen(false); }
        } catch {
            setUser(null);
            setDialogOpen(false);
        }
    }, [location.pathname]);

    const { activePopup, dismissCurrent, loading } = useSystemPopupQueue(user);

    // Open Passkey / Compliance dialogs when their queue step arrives; PasswordChangeFlow manages its own open state
    useEffect(() => {
        dismissingRef.current = false; // reset guard for each new queue step
        if (activePopup && activePopup !== POPUP_KEYS.CHANGE_PASSWORD && !loading) {
            addTimer(() => setDialogOpen(true), 30);
        } else {
            setDialogOpen(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activePopup, loading]);

    // While the forced password-reset popup is active:
    //  • Hold the session for 30 days so it cannot expire mid-form.
    //  • Set a flag so the activity-guard skips CheckSessionActivity polling
    //    and the warn-window extension logic entirely — both are irrelevant
    //    while a 30-day hold is in place.
    // logout() → localStorage.clear() wipes both keys automatically after submit.
    useEffect(() => {
        if (activePopup === POPUP_KEYS.CHANGE_PASSWORD) {
            const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000;
            localStorage.setItem('_session_expiry_override', (Date.now() + ONE_MONTH_MS).toString());
            localStorage.setItem('_pwd_reset_active', '1');
        }
    }, [activePopup]);

    // Scroll-lock body while compliance is visible
    useEffect(() => {
        const shouldLock = activePopup === POPUP_KEYS.COMPLIANCE && dialogOpen;
        document.body.classList.toggle('hide-scroll', shouldLock);
        return () => document.body.classList.remove('hide-scroll');
    }, [activePopup, dialogOpen]);

    // ── Graceful close helpers (Passkey / Compliance) ───────────────────────────

    const gracefulClose = useCallback((callback) => {
        if (dismissingRef.current) return;
        dismissingRef.current = true;
        setDialogOpen(false);
        addTimer(() => {
            dismissingRef.current = false;
            callback();
        }, CLOSE_MS);
    }, [addTimer]);

    const handleDismiss = useCallback(() => {
        gracefulClose(dismissCurrent);
    }, [gracefulClose, dismissCurrent]);

    // P1: forced password change completed — success screen already informed the user,
    // so just logout and navigate directly to login (no session-ended modal needed).
    const handlePasswordSuccess = useCallback(() => {
        userService.logout();
        window.location.replace('/login');
    }, []);

    // Passkey onboarding
    const handlePasskeyOpenChange = useCallback((open) => {
        if (!open) handleDismiss();
    }, [handleDismiss]);

    const handlePasskeyRegistered = useCallback(() => {
        clearPasskeyPromptFromStoredUser();
        handleDismiss();
    }, [handleDismiss]);

    if (!user || loading) return null;
    if (!activePopup) return null;

    // ── P1: Password change (mandatory, single Dialog — no blink) ──────────────
    if (activePopup === POPUP_KEYS.CHANGE_PASSWORD) {
        return (
            <>
                {/* Block all in-app navigation until the password is changed */}
                <Prompt when message="__MANDATORY_POPUP_BLOCK__" />
                <PasswordChangeFlow open onSuccess={handlePasswordSuccess} />
            </>
        );
    }

    // ── Passkey (after password when applicable) ─────────────────────────────────
    if (activePopup === POPUP_KEYS.CREATE_PASSKEY) {
        return (
            <PasskeyOnboardingModal
                open={dialogOpen}
                onOpenChange={handlePasskeyOpenChange}
                userId={user.id}
                username={user.email || user.username}
                onRegistered={handlePasskeyRegistered}
            />
        );
    }

    // ── Compliance (last when applicable) ────────────────────────────────────────
    if (activePopup === POPUP_KEYS.COMPLIANCE) {
        return (
            <CompliancePopup
                open={dialogOpen}
                text="Compliance FAQs"
                sub="Frequently Asked Questions on Glic Health Compliance. Please read through and acknowledge."
                closePopup={handleDismiss}
            />
        );
    }

    return null;
}
